import React from 'react';
import { IconBuilding, IconExclamationCircle } from './OnboardingIcons';

// Scope note: this step collects only what discovery actually uses.
//
// MX-Port Leo v1.0 §3.2.2.2 requires exactly three things of a company entry -
// a non-empty "name", a "domain", and at least one AAS infrastructure endpoint.
// Everything else here is optional context that either appears in the provider
// card or helps a person recognise the company they are looking at.
//
// Removed: VAT number, tax number, commercial register number, IBAN, BIC,
// account holder, founding year, order currency. None were read anywhere in the
// backend (grep: zero references), none appear in the spec, and asking a company
// for its bank details in order to publish a discovery endpoint is a barrier to
// registration, not a feature. Industries and product groups also went: they
// duplicate the capability keywords collected in a later step, and the spec
// models capabilities as ECLASS classes rather than free text.
const OrganizationInfoForm = ({ data, errors, onUpdate }) => {
  const handleInputChange = (field, value) => {
    onUpdate(field, value);
  };

  const handleNestedChange = (parent, field, value) => {
    onUpdate(parent, {
      ...data[parent],
      [field]: value
    });
  };

  return (
    <div className="form-section">
      <div className="section-header">
        <h3>
          <IconBuilding aria-hidden />
          Company Information
        </h3>
        <p>How your company appears to others searching the data space.</p>
      </div>

      <div className="form-grid">
        <div className="form-group full-width">
          <label htmlFor="company_name" className="form-label required">
            Company Name
          </label>
          <input
            type="text"
            id="company_name"
            className={`form-input ${errors.company_name ? 'error' : ''}`}
            value={data.company_data.company_name}
            onChange={(e) => handleNestedChange('company_data', 'company_name', e.target.value)}
            placeholder="e.g., IDTA - Industrial Digital Twin Association"
            required
          />
          {errors.company_name && (
            <div className="error-message">
              <IconExclamationCircle aria-hidden />
              {errors.company_name}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="domain" className="form-label required">
            Primary Domain
          </label>
          <input
            type="text"
            id="domain"
            className={`form-input ${errors.domain ? 'error' : ''}`}
            value={data.domain}
            onChange={(e) => handleInputChange('domain', e.target.value)}
            placeholder="e.g., admin-shell-io.com"
            required
          />
          {/* The domain is the entry's primary key (spec §3.2.2.1), so it is
              worth saying so before someone types a URL here. */}
          <p className="form-hint">
            Identifies your company in the data space and cannot be changed later.
          </p>
          {errors.domain && (
            <div className="error-message">
              <IconExclamationCircle aria-hidden />
              {errors.domain}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="homepage_url" className="form-label">
            Website
          </label>
          <input
            type="url"
            id="homepage_url"
            className={`form-input ${errors.homepage_url ? 'error' : ''}`}
            value={data.company_data.homepage_url}
            onChange={(e) => handleNestedChange('company_data', 'homepage_url', e.target.value)}
            placeholder="e.g., https://www.idtwin.org"
          />
          {errors.homepage_url && (
            <div className="error-message">
              <IconExclamationCircle aria-hidden />
              {errors.homepage_url}
            </div>
          )}
        </div>

        <div className="form-group full-width">
          <label htmlFor="company_description" className="form-label">
            Description
          </label>
          <input
            type="text"
            id="company_description"
            className="form-input"
            value={data.company_data.company_description}
            onChange={(e) => handleNestedChange('company_data', 'company_description', e.target.value)}
            placeholder="e.g., Industrial Digital Twin Association"
          />
          <p className="form-hint">
            One line shown on your provider card in search results.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrganizationInfoForm;
